/* =========================================================
   FILE: cn-header-runtime.js
   Cardcomplex Header Runtime

   - Sticky Header
   - Native Cart Notification Bridge
   - Membership Dropdown
   - Menu Drawer Blur / Geometry
   - Reflow-optimiert
========================================================= */

(() => {
  'use strict';

  /* =========================
     STICKY HEADER
  ========================= */

  if (!customElements.get('sticky-header')) {
    class StickyHeader extends HTMLElement {
      connectedCallback() {
        this.header = document.querySelector('.section-header');
        if (!this.header) return;

        this.headerIsAlwaysSticky =
          this.getAttribute('data-sticky-type') === 'always' ||
          this.getAttribute('data-sticky-type') === 'reduce-logo-size';

        this.headerBounds = {
          top: 0,
          bottom: 0
        };
        this.currentScrollTop = window.scrollY || 0;
        this.preventReveal = false;

        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => {
          this.preventReveal = true;
        };

        /*
          Keine synchrone offsetHeight-Abfrage mehr.
          ResizeObserver liefert die Header-Geometrie asynchron,
          ohne an dieser Stelle einen Layout-Flush zu erzwingen.
        */
        if ('ResizeObserver' in window) {
          this.headerResizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;

            const borderBoxSize = entry.borderBoxSize?.[0];
            const height =
              borderBoxSize?.blockSize ||
              entry.contentRect.height;

            if (!height) return;

            document.documentElement.style.setProperty(
              '--header-height',
              `${Math.round(height)}px`
            );
          });

          this.headerResizeObserver.observe(this.header);
        }

        if (this.headerIsAlwaysSticky) {
          this.header.classList.add(
            'shopify-section-header-sticky'
          );
        }

        this.addEventListener(
          'preventHeaderReveal',
          this.hideHeaderOnScrollUp
        );

        window.addEventListener(
          'scroll',
          this.onScrollHandler,
          { passive: true }
        );

        this.createObserver();
      }

      disconnectedCallback() {
        this.removeEventListener(
          'preventHeaderReveal',
          this.hideHeaderOnScrollUp
        );

        window.removeEventListener(
          'scroll',
          this.onScrollHandler
        );

        this.headerResizeObserver?.disconnect();
      }

      createObserver() {
        const observer = new IntersectionObserver(
          (entries, instance) => {
            const entry = entries[0];

            if (entry) {
              this.headerBounds = {
                top: entry.boundingClientRect.top,
                bottom:
                  entry.boundingClientRect.top +
                  entry.boundingClientRect.height
              };
            }

            instance.disconnect();
          }
        );

        observer.observe(this.header);
      }

      onScroll() {
        const scrollTop =
          window.scrollY ||
          document.documentElement.scrollTop ||
          0;

        const searchIsOpen =
          this.querySelector('.header__search details[open]') ||
          document.body.classList.contains('cn-search-open') ||
          document.body.classList.contains(
            'cn-mobile-search-fixed-open'
          );

        if (searchIsOpen) {
          this.currentScrollTop = scrollTop;
          return;
        }

        if (
          scrollTop > this.currentScrollTop &&
          scrollTop > this.headerBounds.bottom
        ) {
          this.header.classList.add('scrolled-past-header');

          if (!this.preventHide) {
            requestAnimationFrame(() => this.hide());
          }
        } else if (
          scrollTop < this.currentScrollTop &&
          scrollTop > this.headerBounds.bottom
        ) {
          this.header.classList.add('scrolled-past-header');

          if (!this.preventReveal) {
            requestAnimationFrame(() => this.reveal());
          } else {
            clearTimeout(this.isScrolling);

            this.isScrolling = setTimeout(() => {
              this.preventReveal = false;
            }, 66);

            requestAnimationFrame(() => this.hide());
          }
        } else if (scrollTop <= this.headerBounds.top) {
          this.header.classList.remove('scrolled-past-header');
          requestAnimationFrame(() => this.reset());
        }

        this.currentScrollTop = scrollTop;
      }

      hide() {
        if (this.headerIsAlwaysSticky) return;

        this.header.classList.add(
          'shopify-section-header-hidden',
          'shopify-section-header-sticky'
        );

        this.closeMenuDisclosure();
        this.closeSearchModal();
      }

      reveal() {
        if (this.headerIsAlwaysSticky) return;

        this.header.classList.add(
          'shopify-section-header-sticky',
          'animate'
        );
        this.header.classList.remove(
          'shopify-section-header-hidden'
        );
      }

      reset() {
        if (this.headerIsAlwaysSticky) return;

        this.header.classList.remove(
          'shopify-section-header-hidden',
          'shopify-section-header-sticky',
          'animate'
        );
      }

      closeMenuDisclosure() {
        this.disclosures ||=
          this.header.querySelectorAll('header-menu');

        this.disclosures.forEach((disclosure) => {
          disclosure.close();
        });
      }

      closeSearchModal() {
        this.searchModal ||=
          this.header.querySelector('details-modal');

        this.searchModal?.close(false);
      }
    }

    customElements.define('sticky-header', StickyHeader);
  }

  /* =========================
     CART NOTIFICATION BRIDGE
  ========================= */

  const initCartBridge = () => {
    const nativeCartNotification = document.querySelector(
      '.cn-native-cart-notification-bridge cart-notification'
    );

    if (
      !nativeCartNotification ||
      nativeCartNotification.dataset.cnBridgeReady === 'true'
    ) {
      return;
    }

    nativeCartNotification.dataset.cnBridgeReady = 'true';

    nativeCartNotification.open = () => {
      document.dispatchEvent(
        new CustomEvent('cart:updated')
      );
      document.dispatchEvent(
        new CustomEvent('cart:refresh')
      );
    };

    nativeCartNotification.close = () => {};
  };

  /* =========================
     MEMBER BENEFITS DROPDOWN
  ========================= */

  const initMemberBenefits = () => {
    if (window.cnMemberBenefitsDropdownBound) return;
    window.cnMemberBenefitsDropdownBound = true;

    const rootSelector = '[data-cn-member-benefits]';
    const buttonSelector =
      '[data-cn-member-benefits-button]';
    const panelSelector =
      '[data-cn-member-benefits-panel]';

    let openRoot = document.querySelector(
      `${rootSelector}.is-open`
    );

    const closeRoot = (root) => {
      if (!root) return;

      root.classList.remove('is-open');

      root
        .querySelector(buttonSelector)
        ?.setAttribute('aria-expanded', 'false');

      const panel = root.querySelector(panelSelector);
      if (panel) panel.hidden = true;

      if (openRoot === root) openRoot = null;
    };

    const closeAll = (exceptRoot = null) => {
      if (openRoot && openRoot !== exceptRoot) {
        closeRoot(openRoot);
      }
    };

    const toggleRoot = (root) => {
      if (!root) return;

      const button = root.querySelector(buttonSelector);
      const panel = root.querySelector(panelSelector);
      const willOpen = root !== openRoot;

      closeAll(root);

      if (!willOpen) {
        closeRoot(root);
        return;
      }

      root.classList.add('is-open');
      button?.setAttribute('aria-expanded', 'true');

      if (panel) panel.hidden = false;

      openRoot = root;
    };

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const root = target.closest(rootSelector);
        const button = target.closest(buttonSelector);

        if (button && root) {
          event.preventDefault();
          event.stopPropagation();
          toggleRoot(root);
          return;
        }

        if (!root) closeAll();
      },
      true
    );

    document.addEventListener(
      'touchstart',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        if (target.closest(rootSelector)) {
          event.stopPropagation();
        }
      },
      true
    );

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAll();
    });

    const desktopQuery = window.matchMedia(
      '(min-width: 750px)'
    );

    let lastScrollY = window.scrollY || 0;
    let scrollFrame = 0;

    window.addEventListener(
      'scroll',
      () => {
        if (!desktopQuery.matches || !openRoot || scrollFrame) {
          return;
        }

        scrollFrame = requestAnimationFrame(() => {
          scrollFrame = 0;

          const currentScrollY = window.scrollY || 0;

          if (currentScrollY > lastScrollY + 1) {
            closeAll();
          }

          lastScrollY = currentScrollY;
        });
      },
      { passive: true }
    );
  };

  /* =========================
     DRAWER BLUR / GEOMETRY

     Wichtig:
     Solange der Drawer geschlossen ist, wird KEINE
     getBoundingClientRect()-Abfrage ausgeführt.
  ========================= */

  const initDrawerBlur = () => {
    if (window.cnDrawerRealBlurBound) return;

    const drawerDetails = document.querySelector(
      '#Details-menu-drawer-container'
    );
    const drawer = document.querySelector('#menu-drawer');

    if (!drawerDetails || !drawer) return;

    window.cnDrawerRealBlurBound = true;

    const root = document.documentElement;

    let frameId = 0;
    let isDrawerOpen = false;

    const getViewportHeight = () =>
      window.visualViewport?.height ||
      window.innerHeight;

    const readAndWriteGeometry = () => {
      frameId = 0;

      const nextOpenState =
        drawerDetails.hasAttribute('open') &&
        drawerDetails.classList.contains('menu-opening');

      /*
        Geschlossener Drawer:
        nur State aktualisieren und sofort abbrechen.
        Dadurch entfällt der teure Geometrie-Read beim Seitenstart.
      */
      if (!nextOpenState) {
        isDrawerOpen = false;

        if (
          document.body.classList.contains(
            'cn-header-drawer-open'
          )
        ) {
          document.body.classList.remove(
            'cn-header-drawer-open'
          );
        }

        return;
      }

      isDrawerOpen = true;

      /*
        PHASE 1: Geometrie lesen.
      */
      const rect = drawer.getBoundingClientRect();
      const top = Math.max(0, Math.round(rect.top));
      const viewportHeight = getViewportHeight();
      const availableHeight = Math.max(
        0,
        Math.round(viewportHeight - top)
      );

      /*
        PHASE 2: Styles schreiben.
      */
      root.style.setProperty(
        '--cn-drawer-glass-top',
        `${top}px`
      );
      root.style.setProperty(
        '--cn-drawer-glass-height',
        `${availableHeight}px`
      );
      root.style.setProperty(
        '--cn-drawer-panel-height',
        `${availableHeight + 12}px`
      );

      if (
        !document.body.classList.contains(
          'cn-header-drawer-open'
        )
      ) {
        document.body.classList.add(
          'cn-header-drawer-open'
        );
      }
    };

    const schedule = () => {
      if (frameId) return;

      frameId = requestAnimationFrame(
        readAndWriteGeometry
      );
    };

    const observer = new MutationObserver(schedule);

    observer.observe(drawerDetails, {
      attributes: true,
      attributeFilter: ['open', 'class']
    });

    /*
      Resize/Viewport-Änderungen sind nur relevant,
      wenn der Drawer tatsächlich offen ist.
    */
    const scheduleIfOpen = () => {
      if (isDrawerOpen) schedule();
    };

    window.addEventListener(
      'resize',
      scheduleIfOpen,
      { passive: true }
    );

    window.addEventListener(
      'orientationchange',
      scheduleIfOpen,
      { passive: true }
    );

    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        'resize',
        scheduleIfOpen,
        { passive: true }
      );

      window.visualViewport.addEventListener(
        'scroll',
        scheduleIfOpen,
        { passive: true }
      );
    }

    /*
      Kein initiales schedule():
      der Drawer ist beim First Paint geschlossen.
      Erst die Mutation beim Öffnen startet die Geometrie-Berechnung.
    */
  };

  const init = () => {
    initCartBridge();
    initMemberBenefits();
    initDrawerBlur();
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }

  document.addEventListener(
    'shopify:section:load',
    init
  );
})();
